/**
 * 评测配置 › 判分规则（`#eval-rules`，后台页面对齐批 4；只读）：评测判分口径注册表 `plan_lab/eval/scoring_rules.py`。
 * 照「运行规则」页的样子：按类型筛，点一行看全部字段。判分代码直接引用注册表里的值，页面和代码读同一处；
 * 改口径走 PR，同 PR 改注册表。判官提示词和模型不在这里（随题库快照，见「判官提示」页）。
 * 查询参数 `rule`：判官提示页点规则编号跳过来，直接打开那条的抽屉；关抽屉时去掉参数。
 */
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

import { Badge, type BadgeTone } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Drawer, DrawerSection } from "@/components/ui/drawer";
import { EmptyState } from "@/components/ui/feedback";
import { LabeledSelect } from "@/components/ui/select";
import { type PlanScoringRule, usePlanScoringRules } from "@/lib/api/plan-eval-config";

import { PageJump } from "../kit/page-jump";
import { type PlanColumn, PlanTable } from "../kit/plan-table";
import { BlockState, Field } from "./shared";

const ALL = "__all__";

const KIND_TONE: Record<string, BadgeTone> = {
	判分: "info",
	一票否决: "danger",
	分母口径: "warning",
	上限与闸门: "neutral",
	观测列: "muted",
	测试集: "accent",
};

const COLUMNS: PlanColumn<PlanScoringRule>[] = [
	{
		key: "id",
		title: "编号",
		className: "w-20",
		render: (r) => <span className="font-mono">{r.id}</span>,
	},
	{
		key: "kind",
		title: "类型",
		className: "w-24",
		render: (r) => <Badge tone={KIND_TONE[r.kind] ?? "muted"}>{r.kind}</Badge>,
	},
	{
		key: "name",
		title: "名称",
		className: "max-w-[160px]",
		render: (r) => (
			<span className="block truncate font-medium" title={r.name}>
				{r.name}
			</span>
		),
	},
	{
		key: "does",
		title: "做什么",
		className: "max-w-[300px]",
		render: (r) => (
			<span className="block truncate text-text-secondary" title={r.does}>
				{r.does}
			</span>
		),
	},
	{
		key: "value",
		title: "值",
		className: "max-w-[220px]",
		render: (r) => (
			<span className="block truncate" title={r.value}>
				{r.value}
			</span>
		),
	},
	{
		key: "where",
		title: "代码位置",
		className: "max-w-[170px]",
		render: (r) => (
			<span className="block truncate font-mono text-xs text-text-muted" title={r.where}>
				{r.where}
			</span>
		),
	},
];

export function EvalRulesTab({ focusId }: { focusId?: string } = {}) {
	const q = usePlanScoringRules();
	const navigate = useNavigate();
	const [kind, setKind] = useState(ALL);
	const [open, setOpen] = useState<PlanScoringRule | null>(null);
	const data = q.data;
	useEffect(() => {
		if (!focusId || !data) return;
		const hit = data.rules.find((r) => r.id === focusId);
		if (hit) setOpen(hit);
	}, [focusId, data]);
	const close = () => {
		setOpen(null);
		if (focusId) navigate({ to: "/plan-lab", search: {}, hash: "eval-rules", replace: true });
	};
	const rows = useMemo(
		() => (data?.rules ?? []).filter((r) => kind === ALL || r.kind === kind),
		[data, kind],
	);

	if (q.isLoading || q.isError || !data)
		return <BlockState testId="plan-block-eval-rules" q={q} failTestId="plan-eval-rules-fail" />;

	return (
		<Card data-testid="plan-block-eval-rules">
			<CardContent className="space-y-3 py-4" data-testid="plan-eval-rules">
				<div className="flex flex-wrap items-center gap-3">
					<LabeledSelect
						label="类型"
						value={kind}
						onChange={setKind}
						options={[
							{ value: ALL, label: "全部" },
							...data.kinds.map((k) => ({ value: k, label: k })),
						]}
					/>
					<span className="text-xs text-text-muted">
						只读：判分代码直接读这张表的值，改口径走 PR；三维结论来自 <PageJump to="eval-judges" />
						；事项对齐用 <PageJump to="eval-bank" /> 金标的诉求编号
					</span>
					<span className="ml-auto text-xs text-text-muted">共 {rows.length} 条</span>
				</div>
				{rows.length ? (
					<PlanTable
						columns={COLUMNS}
						rows={rows}
						rowKey={(r) => r.id}
						onRowClick={setOpen}
						density="compact"
						rowTestId="plan-eval-rule-row"
					/>
				) : (
					<EmptyState title="没有这类规则" desc="换个类型看看。" />
				)}
			</CardContent>
			<Drawer
				open={!!open}
				onClose={close}
				width="min(720px,95vw)"
				testId="plan-eval-rule-drawer"
				title={
					<span>
						<span className="font-mono">{open?.id}</span> {open?.name}
					</span>
				}
				description={open?.kind}
			>
				{open ? (
					<>
						<DrawerSection title="规则说明">
							<Field label="做什么">{open.does}</Field>
							<Field label="值">{open.value}</Field>
						</DrawerSection>
						<DrawerSection title="代码位置">
							<ul className="space-y-1 font-mono text-xs text-text-primary">
								{open.where.split("；").map((w) => (
									<li key={w}>{w}</li>
								))}
							</ul>
						</DrawerSection>
					</>
				) : null}
			</Drawer>
		</Card>
	);
}
